import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import {
  RiArrowLeftSLine,
  RiHome5Line,
  RiBankCardLine,
  RiCheckLine,
  RiNotification3Line,
  RiFlashlightLine,
  // TODO(account-hide): 聯絡發卡銀行 / 安全鎖 icon，待實作後恢復。詳見 docs/account-hidden-sections.md
  // RiCustomerService2Line,
  // RiShieldCheckLine,
  // RiFileDownloadLine,（進階管理隱藏中）
  RiDeleteBinLine,
  RiErrorWarningLine,
} from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";

import SectionLabel from "../ui/SectionLabel";
import ListContainer from "../ui/ListContainer";
import DetailRow from "../ui/DetailRow";
import ToggleRow from "../ui/ToggleRow";
import StatBox from "../ui/StatBox";
import FnRow from "../ui/FnRow";
import InfoNote from "../ui/InfoNote";
import MethodCardVisual from "./MethodCardVisual";
import { detectCard } from "./Common";
import type { MethodAccountUsage, MethodRecentTx, MethodStats, PaymentMethodDetailData } from "./methodTypes";
import {
  handleAPIGetPaymentMethodDetail,
  handleAPIListCardPayments,
  handleAPIUpdatePaymentMethod,
  handleAPISetContextDefault,
  handleAPIUnbindPaymentMethod,
  type OwnedPaymentMethod,
  type CardPaymentItem,
} from "../../API/handlePaymentAPI";
import BottomSheet from "../ui/BottomSheet";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { appToast } from "@components/Toast";

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** API 單卡詳情 → 詳細頁卡面/資訊 */
const mapDetail = (d: OwnedPaymentMethod): PaymentMethodDetailData => {
  const isCard = d.method_type === "credit_card";
  const card = d.card;
  const net = detectCard(card?.bincode ?? "")?.name;
  const masked = card
    ? `${(card.bincode ?? "").slice(0, 4)} ${(card.bincode ?? "").slice(4, 6)}•• •••• ${card.lastfour ?? "••••"}`.trim()
    : undefined;
  return {
    id: d.payment_method_id,
    kind: isCard ? "card" : "ewallet",
    name: d.nickname ?? (isCard ? "信用卡" : d.method_type),
    iconText: isCard ? "C" : "$",
    color: colors.primary,
    bank: card?.issuer ?? undefined,
    fullName: d.nickname ?? card?.issuer ?? undefined,
    cardNumber: masked,
    expiry: card?.expiry_month ? `${pad2(card.expiry_month)}/${String(card.expiry_year ?? "").slice(-2)}` : undefined,
    network: net,
  };
};

/** API 單卡付款 → 最近扣款列 */
const mapTx = (t: CardPaymentItem): MethodRecentTx => {
  const d = new Date(t.created_at);
  return {
    id: t.payment_transaction_id,
    desc: t.card_label ?? "扣款",
    merchant: t.display_id ? `訂單 ${t.display_id}` : "訂單",
    date: `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
    amount: t.amount ?? 0,
  };
};

/**
 * 付款方式詳細頁（從現有 CardList 點卡片進入）。
 * 接 GET /customer/payment-methods/:id（卡資訊）+ /payments（最近扣款，spec API3）。
 * 「各帳戶啟用」需 per-context 端點，暫留空（TODO API）。
 */
export const PaymentMethodDetail = () => {
  const navigate = useNavigate();
  const { methodId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const [method, setMethod] = useState<PaymentMethodDetailData | null>(null);
  const [recentTx, setRecentTx] = useState<MethodRecentTx[]>([]);
  const [stats, setStats] = useState<MethodStats>({ accountCount: 0, monthCount: 0, monthAmount: 0 });
  const [loading, setLoading] = useState(true);

  const [accounts, setAccounts] = useState<MethodAccountUsage[]>([]);
  const [autoFill, setAutoFill] = useState(false);
  const [notify, setNotify] = useState(true);
  // 此卡已啟用（有授權）的帳戶、以及目前以此卡為預設的帳戶
  const [authCtxIds, setAuthCtxIds] = useState<number[]>([]);
  const [defaultCtxIds, setDefaultCtxIds] = useState<number[]>([]);
  const [savingNotify, setSavingNotify] = useState(false);
  const [savingAutoFill, setSavingAutoFill] = useState(false);
  // 移除確認 BottomSheet
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!cusJwtToken || !methodId) {
      setLoading(false);
      return;
    }
    let active = true;
    Promise.all([
      handleAPIGetPaymentMethodDetail(methodId, cusJwtToken),
      handleAPIListCardPayments(methodId, cusJwtToken, 1, 10),
    ])
      .then(([d, p]) => {
        if (!active) return;
        if (d.state === "success" && d.data) {
          setMethod(mapDetail(d.data));
          setNotify(d.data.notify_on_charge ?? true);
          const ctxIds = Array.from(new Set((d.data.authorizations ?? []).map((a) => a.context_id)));
          const defIds = d.data.default_context_ids ?? [];
          setAuthCtxIds(ctxIds);
          setDefaultCtxIds(defIds);
          // 自動代入 = 至少一個帳戶以此卡為預設付款
          setAutoFill(defIds.length > 0);
        }
        if (p.state === "success") {
          setRecentTx(p.items.map(mapTx));
          setStats((s) => ({
            ...s,
            monthCount: p.items.length,
            monthAmount: p.items.reduce((a, t) => a + (t.amount ?? 0), 0),
          }));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, methodId]);

  const back = () => navigate("/account/paymentMethod");

  const toggleAccount = (id: string) =>
    setAccounts((prev) => prev.map((a) => (a.id === id && a.accepts ? { ...a, enabled: !a.enabled } : a)));

  // 移除付款方式：DELETE（後端軟刪除——通知 TapPay 刪卡、清各帳戶預設、狀態改 revoked；
  // 歷史扣款紀錄與既有訂單快照不受影響）
  const handleRemove = async () => {
    if (!methodId || !cusJwtToken || removing) return;
    setRemoving(true);
    const r = await handleAPIUnbindPaymentMethod(methodId, cusJwtToken);
    setRemoving(false);
    if (r.state === "success") {
      appToast.success("已移除付款方式");
      setRemoveOpen(false);
      navigate("/account/paymentMethod", { replace: true });
    } else {
      appToast.error(r.error || "移除失敗，請稍後再試");
    }
  };

  // 扣款推播通知：PATCH notify_on_charge（樂觀更新、失敗回滾）
  const toggleNotify = async (v: boolean) => {
    if (!methodId || !cusJwtToken || savingNotify) return;
    setNotify(v);
    setSavingNotify(true);
    const r = await handleAPIUpdatePaymentMethod(methodId, { notify_on_charge: v }, cusJwtToken);
    setSavingNotify(false);
    if (r.state !== "success") {
      setNotify(!v);
      appToast.error("設定失敗，請稍後再試");
    }
  };

  // 自動代入：開 → 把此卡設為所有已啟用帳戶的預設付款；關 → 清掉以此卡為預設的帳戶
  const toggleAutoFill = async (v: boolean) => {
    if (!methodId || !cusJwtToken || savingAutoFill) return;
    if (v && authCtxIds.length === 0) {
      appToast.error("此卡尚未啟用於任何帳戶，無法自動代入");
      return;
    }
    setAutoFill(v);
    setSavingAutoFill(true);
    const targets = v ? authCtxIds : defaultCtxIds;
    const results = await Promise.all(targets.map((ctxId) => handleAPISetContextDefault(ctxId, v ? methodId : null, cusJwtToken)));
    setSavingAutoFill(false);
    const okIds = targets.filter((_, i) => results[i].state === "success");
    const failCount = targets.length - okIds.length;
    if (v) {
      setDefaultCtxIds(okIds);
      if (okIds.length === 0) {
        setAutoFill(false);
        appToast.error("設定失敗，請稍後再試");
      } else if (failCount > 0) {
        appToast.error(`部分帳戶設定失敗（${failCount} 個），請稍後再試`);
      }
    } else {
      const remaining = defaultCtxIds.filter((id) => !okIds.includes(id));
      setDefaultCtxIds(remaining);
      if (remaining.length > 0) {
        setAutoFill(true);
        appToast.error("部分帳戶取消失敗，請稍後再試");
      }
    }
  };

  if (loading) {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        <Header>
          <XStack ai="center" onPress={back} cursor="pointer">
            <View mr="$2">
              <RiArrowLeftSLine size={20} color={colors.light} />
            </View>
            <Text color={colors.light} fontWeight="bold" fontSize={16}>
              付款方式
            </Text>
          </XStack>
        </Header>
        <YStack f={1} ai="center" jc="center">
          <Spinner size="large" color={colors.primary} />
        </YStack>
      </YStack>
    );
  }

  if (!method) {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        <Header>
          <XStack ai="center" onPress={back} cursor="pointer">
            <View mr="$2">
              <RiArrowLeftSLine size={20} color={colors.light} />
            </View>
            <Text color={colors.light} fontWeight="bold" fontSize={16}>
              付款方式
            </Text>
          </XStack>
        </Header>
        <YStack f={1} ai="center" jc="center" gap="$2">
          <Text fontSize={14} color={colors.sub}>
            查無此付款方式
          </Text>
          <Text fontSize={12} color={colors.hint}>
            編號 {methodId ?? "-"}
          </Text>
        </YStack>
      </YStack>
    );
  }

  const accent = method.color ?? colors.primary;
  const isCard = method.kind === "card";
  const enabledCount = accounts.filter((a) => a.enabled).length;

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={back} cursor="pointer" mb="$3.5">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            付款方式
          </Text>
        </XStack>
        <MethodCardVisual data={method} />
      </Header>

      <RadiusContentWrapper space="$4">
        {/* 使用概況 */}
        <YStack>
          <SectionLabel title="使用概況" />
          <XStack gap="$2">
            <View f={1}>
              <StatBox label="啟用帳戶" value={`${stats.accountCount}`} sub="個瓦斯帳戶" />
            </View>
            <View f={1}>
              <StatBox label="本月扣款" value={`${stats.monthCount}`} sub="筆交易" />
            </View>
            <View f={1}>
              <StatBox label="本月金額" value={`$${stats.monthAmount.toLocaleString()}`} sub="累計" valueColor={accent} />
            </View>
          </XStack>
        </YStack>

        {/* 啟用於哪些帳戶 */}
        <YStack>
          <SectionLabel title={`啟用於 (${enabledCount} / ${accounts.length})`} right={<Text fontSize={10} color={colors.hint}>點開關直接切換</Text>} />
          <ListContainer>
            {accounts.length === 0 ? (
              <Text fontSize={13} color={colors.hint} ta="center" py="$6">
                尚未啟用於任何帳戶
              </Text>
            ) : (
              accounts.map((a, i) => (
                <XStack key={a.id} ai="center" gap="$3" px="$3.5" py="$3" bbw={i === accounts.length - 1 ? 0 : 1} bbc={colors.borderLight} opacity={a.accepts ? 1 : 0.55}>
                  <View w={32} h={32} br={9} bg={`${a.color}18`} ai="center" jc="center" flexShrink={0}>
                    <RiHome5Line size={16} color={a.color} />
                  </View>
                  <YStack f={1} miw={0}>
                    <XStack ai="center" gap="$1.5">
                      <Text fontSize={13} fontWeight="700" color={colors.text}>
                        {a.label}
                      </Text>
                      {!a.accepts && (
                        <View br={5} px="$1.5" bg={colors.bgRed}>
                          <Text fontSize={9} fontWeight="700" color={colors.danger}>
                            不接受
                          </Text>
                        </View>
                      )}
                    </XStack>
                    <Text fontSize={11} color={colors.hint} mt="$0.5">
                      {a.accepts ? a.vendor : `${a.vendor}不支援`}
                    </Text>
                  </YStack>
                  <View
                    w={44}
                    h={26}
                    br={13}
                    bg={a.enabled ? a.color : colors.gray100}
                    position="relative"
                    cursor={a.accepts ? "pointer" : "not-allowed"}
                    flexShrink={0}
                    opacity={a.accepts ? 1 : 0.5}
                    onPress={() => toggleAccount(a.id)}
                    style={{ transition: "background 0.2s" }}>
                    <View w={22} h={22} br={11} bg={colors.light} position="absolute" t={2} l={a.enabled ? 20 : 2} style={{ transition: "left 0.2s", boxShadow: "0 2px 4px rgba(0,0,0,0.15)" }} />
                  </View>
                </XStack>
              ))
            )}
          </ListContainer>
        </YStack>

        {/* 卡片 / 帳號資訊 */}
        <YStack>
          <SectionLabel title={isCard ? "卡片資訊" : "帳號資訊"} />
          <ListContainer>
            {isCard ? (
              <>
                <DetailRow label="卡片名稱" value={method.fullName} />
                <DetailRow label="卡號" value={method.cardNumber} mono />
                <DetailRow label="持卡人" value={method.holder} />
                <DetailRow label="有效期限" value={method.expiry} mono />
                <DetailRow label="發卡銀行" value={method.bank} isLast />
              </>
            ) : (
              <>
                <DetailRow label="帳號名稱" value={method.fullName ?? method.name} />
                <DetailRow label="綁定手機" value={method.bindPhone} />
                {method.bindEmail && <DetailRow label="綁定 Email" value={method.bindEmail} />}
                {method.realName && <DetailRow label="實名" value={method.realName} />}
                <DetailRow label="綁定日期" value={method.bound} isLast />
              </>
            )}
          </ListContainer>
        </YStack>

        {/* 偏好設定 */}
        <YStack>
          <SectionLabel title="偏好設定" />
          <ListContainer>
            <ToggleRow
              label="自動代入此付款方式"
              sub="新訂單預設用這張卡（限已啟用的帳戶）"
              icon={<RiFlashlightLine size={15} color={accent} />}
              iconColor={accent}
              value={autoFill}
              onChange={toggleAutoFill}
              accentColor={accent}
            />
            <ToggleRow
              label="扣款推播通知"
              sub="每次扣款都推播給你"
              icon={<RiNotification3Line size={15} color={colors.amber} />}
              iconColor={colors.amber}
              value={notify}
              onChange={toggleNotify}
              accentColor={accent}
              isLast
            />
          </ListContainer>
        </YStack>

        {/* 最近扣款 */}
        <YStack>
          <SectionLabel
            title={`最近扣款 (${recentTx.length})`}
            right={
              <Text
                fontSize={11}
                fontWeight="700"
                color={accent}
                cursor="pointer"
                onPress={() => navigate(`/account/paymentMethod/detail/${methodId}/payments`)}>
                查看全部
              </Text>
            }
          />
          <ListContainer>
            {recentTx.length === 0 ? (
              <Text fontSize={13} color={colors.hint} ta="center" py="$6">
                還沒有扣款紀錄
              </Text>
            ) : (
              recentTx.map((tx, i) => (
                <XStack key={tx.id} ai="center" gap="$3" px="$3.5" py="$3" bbw={i === recentTx.length - 1 ? 0 : 1} bbc={colors.borderLight} cursor="pointer">
                  <View w={36} h={36} br={9} bg={`${accent}15`} ai="center" jc="center" flexShrink={0}>
                    <RiCheckLine size={16} color={accent} />
                  </View>
                  <YStack f={1} miw={0}>
                    <Text fontSize={13} fontWeight="600" color={colors.text}>
                      {tx.desc}
                    </Text>
                    <Text fontSize={11} color={colors.hint} mt="$0.5">
                      {tx.merchant} · {tx.date} {tx.time}
                    </Text>
                  </YStack>
                  <Text fontSize={14} fontWeight="800" color={colors.text} style={{ fontVariantNumeric: "tabular-nums" }}>
                    ${tx.amount.toLocaleString()}
                  </Text>
                </XStack>
              ))
            )}
          </ListContainer>
        </YStack>

        {/* TODO(account-hide): 進階管理整區（聯絡發卡銀行 / 安全鎖 / 匯出扣款紀錄）尚未實作，暫時隱藏。詳見 docs/account-hidden-sections.md */}
        {/* <YStack>
          <SectionLabel title="進階管理" />
          <ListContainer>
            {isCard && (
              <>
                <FnRow icon={<RiCustomerService2Line size={15} color={colors.green} />} iconColor={colors.green} label="聯絡發卡銀行" sub={method.bankPhone} />
                <FnRow icon={<RiShieldCheckLine size={15} color={colors.primary} />} iconColor={colors.primary} label="安全鎖" sub="暫停此卡的所有扣款" right="正常" />
              </>
            )}
            <FnRow icon={<RiFileDownloadLine size={15} color={colors.amber} />} iconColor={colors.amber} label="匯出扣款紀錄" sub="下載 CSV 或 PDF" isLast />
          </ListContainer>
        </YStack> */}

        {/* 危險區 */}
        <YStack>
          <SectionLabel title="危險區域" />
          <YStack bg={colors.light} br={12} ov="hidden" bw={1} bc={`${colors.danger}30`}>
            <FnRow
              icon={<RiDeleteBinLine size={15} color={colors.danger} />}
              iconColor={colors.danger}
              label="移除此付款方式"
              sub="從錢包移除，不影響歷史扣款紀錄"
              danger
              isLast
              onPress={() => setRemoveOpen(true)}
            />
          </YStack>
          {enabledCount > 0 && (
            <View mt="$2">
              <InfoNote tone="amber" icon={<RiErrorWarningLine size={13} color={colors.amberDark} />}>
                此付款方式正啟用於 {enabledCount} 個瓦斯帳戶。移除前請先取消啟用，或設定其他預設付款。
              </InfoNote>
            </View>
          )}
        </YStack>
      </RadiusContentWrapper>

      {/* 移除確認 */}
      <BottomSheet open={removeOpen} onClose={() => !removing && setRemoveOpen(false)}>
        <YStack px="$4" pb="$5" gap="$3.5">
          <Text fontSize={17} fontWeight="800" color={colors.text}>
            移除此付款方式？
          </Text>
          <Text fontSize={13} color={colors.sub} lineHeight={20}>
            {method.name}
            {method.cardNumber ? `（${method.cardNumber.slice(-4)}）` : ""} 將從你的付款方式中移除，
            並取消所有帳戶以它為預設付款的設定。歷史扣款紀錄與既有訂單不受影響。
          </Text>
          {enabledCount > 0 && (
            <InfoNote tone="amber" icon={<RiErrorWarningLine size={13} color={colors.amberDark} />}>
              此付款方式正啟用於 {enabledCount} 個瓦斯帳戶，移除後這些帳戶將改回貨到付款。
            </InfoNote>
          )}
          <XStack gap="$2.5" mt="$1">
            <YStack
              f={1}
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.border}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={() => !removing && setRemoveOpen(false)}>
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                取消
              </Text>
            </YStack>
            <YStack
              f={1}
              br={12}
              py="$3"
              ai="center"
              bg={colors.danger}
              cursor="pointer"
              opacity={removing ? 0.6 : 1}
              pressStyle={{ opacity: 0.8 }}
              onPress={handleRemove}>
              {removing ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text fontSize={14} fontWeight="700" color="#fff">
                  確認移除
                </Text>
              )}
            </YStack>
          </XStack>
        </YStack>
      </BottomSheet>
    </YStack>
  );
};

export default PaymentMethodDetail;
